import { useEffect, useRef, useState } from 'react'
import { PHISHING_PAYLOAD_TYPE } from '../../domain/domainCodes'
import { Expand, Monitor, Paperclip, ShieldCheck, Smartphone, QrCode } from 'lucide-react'
import Drawer from '../../components/Drawer'
import SegmentedControl from '../../components/SegmentedControl'
import { templatesStrings as S } from './strings'
import { safePreviewHtml } from './safePreviewHtml'
import { useQrPreview } from './useQrPreview'

/**
 * The three stored screenshots a template carries.
 *
 * The legacy list gives each template three image columns — `{id}.png` under
 * PhishingTemplatesEmails, PhishingTemplatesLandingPages and
 * PhishingTemplatesAfterPhishingPages — and clicking one opens that surface in
 * a new tab. The three columns are the whole reason the list is readable: a
 * phishing template IS its email, its landing page and the page the target
 * lands on afterwards, and a name column cannot say which of forty templates
 * is the Absher one.
 *
 * THE CELLS SHOW THE TEMPLATE. They used to show a drawing of a generic window
 * tinted by category — the same three sketches on every row, differing only in
 * hue — because the legacy's `{id}.png` directories are empty here and something
 * had to fill the cell. But a preview whose whole job is to tell forty templates
 * apart cannot be the same picture forty times.
 *
 * The row already carries the real thing: `/filter` returns the saved HTML for
 * all three surfaces, which is what the preview sheet has always rendered. So the
 * cell renders it too, at a readable width and scaled down, and the drawing stays
 * for a surface that genuinely has no HTML.
 *
 * SANDBOXED WITH NO PERMISSIONS, for the reason the sheet gives at length: this is
 * attacker-shaped markup by definition, rendered inside the admin panel by the
 * person who is signed in. An empty `sandbox` lets it paint and nothing else.
 */
const SURFACES = ['email', 'landing', 'after']

/** Each category gets its own hue, so forty rows do not preview as one. */
const HUES = {
  1: 268, // ترفيه
  2: 190, // تأمين
  3: 34, // خصومات
  4: 222, // داخلي
  5: 150, // ليب
  7: 142, // جوائز مالية
  8: 205, // سفر
}

/** The design size every surface is drawn at; the cell and the sheet scale it. */
const W = 320
const H = 200

/* The cell's own box, and the width the surface is laid out at before being scaled into
   it. Rendering the HTML at 104px would reflow it into a column of words; it is drawn at a
   width a page is actually designed for and shrunk, which is what makes the thumbnail look
   like the page. */
const SHOT_W = 104
const SHOT_H = 65
const SHEET_W = 900

/** The saved HTML for one surface, or null when that surface was never authored. */
const surfaceHtml = (template, surface) =>
  ({ email: template.body, landing: template.landingPage, after: template.afterPage }[surface]) ||
  null

export function TemplateShot({ template, surface, onOpen }) {
  const label = `${S.surfaces[surface]} — ${template.name}`
  const html = surfaceHtml(template, surface)

  return (
    <button type="button" className="tpl-shot" onClick={() => onOpen(surface)} title={label}>
      {surface === 'email' && Number(template.payloadType) === PHISHING_PAYLOAD_TYPE.QR_CODE ? (
        <QrCode size={44} aria-hidden="true" />
      ) : html ? (
        /* `pointer-events: none` in the stylesheet, so the click belongs to the button
           around it — a thumbnail is something you press, not something you browse. */
        <span className="tpl-shot__paper" style={{ inlineSize: SHOT_W, blockSize: SHOT_H }}>
          <iframe
            className="tpl-shot__html"
            style={{
              inlineSize: SHEET_W,
              blockSize: SHEET_W * (SHOT_H / SHOT_W),
              scale: SHOT_W / SHEET_W,
            }}
            srcDoc={safePreviewHtml(html)}
            sandbox=""
            loading="lazy"
            tabIndex={-1}
            aria-hidden="true"
            title={label}
          />
        </span>
      ) : (
        <Surface template={template} surface={surface} scale={SHOT_W / W} />
      )}
      <span className="tpl-shot__zoom" aria-hidden="true">
        <Expand size={13} strokeWidth={2} />
      </span>
      <span className="sr-only">{label}</span>
    </button>
  )
}

/**
 * The preview sheet — the legacy's `window.open(ViewEmail?id=)`, except the
 * three surfaces stay together. They are the same story in three frames, and
 * opening each in its own tab was the reason nobody compared them.
 */
export function TemplatePreview({ template, surface, onClose, onSurfaceChange }) {
  const frame = useRef(null)
  const [width, setWidth] = useState(0)
  const [internalSurface, setInternalSurface] = useState('email')
  const [device, setDevice] = useState('desktop')

  const activeSurface = surface || internalSurface
  const qr = Number(template?.payloadType) === PHISHING_PAYLOAD_TYPE.QR_CODE && activeSurface === 'email'
  const preview = useQrPreview(template?.body, qr, template?.isArabic !== false)
  const handleSurfaceChange = onSurfaceChange || setInternalSurface

  /* The sheet is a fraction of the viewport, so the scale cannot be a constant.
     The frame is measured rather than the mat around it — the mat's padding is
     what keeps the surface from running into its own edge. */
  useEffect(() => {
    const node = frame.current
    if (!node) return undefined
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(node)
    return () => observer.disconnect()
  }, [template, device])

  if (!template) return null

  /* From the row, not from a request. `/view-*` would answer with the stored column, which
     is base64 — see the note in phishingService.js. The row already carries all three
     surfaces decoded, so the preview is free and works for an unsaved draft too. */
  const html = qr ? preview.html : { email: template.body, landing: template.landingPage, after: template.afterPage }[activeSurface]

  return (
    <Drawer dialog open size="xl" title={S.templateImages} subtitle={template.name} onClose={onClose}>
      {qr && preview.error && <p role="alert">{S.qrPreviewFailed}</p>}
      <div className="tpl-preview__toolbar">
        <SegmentedControl
          items={SURFACES.map((id) => ({ id, label: S.surfaceTabs[id] }))}
          value={activeSurface}
          onChange={handleSurfaceChange}
          ariaLabel={S.templateImages}
        />
        <div className="tpl-preview__device-toggle" role="group" aria-label="Device View">
          <button
            type="button"
            className={`tpl-preview__device-btn ${device === 'desktop' ? 'is-active' : ''}`}
            onClick={() => setDevice('desktop')}
            title="عرض الحاسب (Desktop)"
            aria-pressed={device === 'desktop'}
          >
            <Monitor size={16} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className={`tpl-preview__device-btn ${device === 'mobile' ? 'is-active' : ''}`}
            onClick={() => setDevice('mobile')}
            title="عرض الهاتف (Mobile)"
            aria-pressed={device === 'mobile'}
          >
            <Smartphone size={16} strokeWidth={1.75} />
          </button>
        </div>
      </div>
      <div className="tpl-preview">
        {/* THE REAL STORED SURFACE, not the drawn stand-in — the row carries the saved
            HTML for all three.

            SANDBOXED WITH NO PERMISSIONS AT ALL. This is attacker-shaped markup by
            definition: it is authored to impersonate a bank or a helpdesk, and it is being
            rendered inside the admin panel by the person who is signed in. An empty
            `sandbox` gives it an opaque origin, no scripts, no forms, no navigation — it can
            paint and nothing else.

            The drawn stand-in is still what the tiny cells use, and what a surface with no
            stored HTML falls back to. */}
        <div className={`tpl-preview__frame ${device === 'mobile' ? 'tpl-preview__frame--mobile' : ''}`} ref={frame}>
          {html ? (
            <iframe
              className={`tpl-preview__html ${device === 'mobile' ? 'tpl-preview__html--mobile' : ''}`}
              srcDoc={safePreviewHtml(html)}
              sandbox=""
              title={S.surfaceTabs[activeSurface]}
            />
          ) : (
            width > 0 && (
              <Surface template={template} surface={activeSurface} scale={Math.min(width / W, 1.6)} />
            )
          )}
        </div>
      </div>
      <p className="tpl-preview__note">{S.surfaceNote}</p>
    </Drawer>
  )
}

/**
 * One surface, drawn at 320×200 and scaled by the caller.
 *
 * Scaling rather than re-authoring at each size is what keeps the cell preview
 * and the sheet preview the same picture — a thumbnail that drops detail is a
 * different image, and the point of the cell is to recognise the sheet in it.
 */
function Surface({ template, surface, scale }) {
  const hue = HUES[template?.categoryId] ?? 222
  const rtl = template?.isArabic ?? true

  return (
    <span
      className={`tpl-surface tpl-surface--${surface}`}
      style={{ '--hue': hue, '--scale': scale, width: W * scale, height: H * scale }}
      aria-hidden="true"
    >
      <span className="tpl-surface__stage" dir={rtl ? 'rtl' : 'ltr'}>
        {surface === 'email' && <EmailSurface template={template} />}
        {surface === 'landing' && <LandingSurface template={template} />}
        {surface === 'after' && <AfterSurface />}
      </span>
    </span>
  )
}

/* ---------------------------------------------------------------- surfaces --
   Drawn from the record: the brand block is the category, the subject line is
   the template's own name, and the payload decides what the body offers —
   a button to a site, an attachment chip, or a form. */

function EmailSurface({ template }) {
  return (
    <>
      <span className="tpl-mail__head">
        <span className="tpl-mail__avatar" />
        <span className="tpl-mail__from">
          <i className="tpl-line tpl-line--strong" style={{ width: 68 }} />
          <i className="tpl-line" style={{ width: 96 }} />
        </span>
      </span>
      <span className="tpl-mail__subject">
        <bdi>{template.name}</bdi>
      </span>
      <span className="tpl-mail__body">
        <i className="tpl-line" style={{ width: '100%' }} />
        <i className="tpl-line" style={{ width: '92%' }} />
        <i className="tpl-line" style={{ width: '74%' }} />
      </span>
      {template.payloadType === PHISHING_PAYLOAD_TYPE.ATTACHMENT ? (
        <span className="tpl-mail__attach">
          <Paperclip size={11} strokeWidth={2} />
          <i className="tpl-line" style={{ width: 54 }} />
        </span>
      ) : (
        <span className="tpl-cta" />
      )}
    </>
  )
}

function LandingSurface({ template }) {
  return (
    <>
      <span className="tpl-chrome">
        <i />
        <i />
        <i />
        <span className="tpl-chrome__url" />
      </span>
      <span className="tpl-page">
        <span className="tpl-page__logo" />
        <i className="tpl-line tpl-line--strong" style={{ width: 132 }} />
        {/* An information request is a form; a site is a form behind a button.
            Either way the landing page's job is the two fields. */}
        <span className="tpl-field" />
        <span className="tpl-field" />
        <span className="tpl-cta" />
        {template.payloadType === PHISHING_PAYLOAD_TYPE.INFORMATION_REQUEST && <i className="tpl-line" style={{ width: 88 }} />}
      </span>
    </>
  )
}

function AfterSurface() {
  return (
    <span className="tpl-after">
      <span className="tpl-after__badge">
        <ShieldCheck size={22} strokeWidth={1.9} />
      </span>
      <i className="tpl-line tpl-line--strong" style={{ width: 150 }} />
      <i className="tpl-line" style={{ width: 190 }} />
      <i className="tpl-line" style={{ width: 148 }} />
      <span className="tpl-cta tpl-cta--calm" />
    </span>
  )
}
